import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/app-shell";
import { CalcShell, Field, NumberInput, Select, ResultCard, fmt } from "@/components/calculators/CalcShell";
import { AddToQuoteButton } from "@/components/calculators/AddToQuoteButton";
import { linesFromElectricianNote } from "@/components/calculators/lineMappers";
import { motorCurrent, type Phase } from "@/domain/calculators";

export const Route = createFileRoute("/_authenticated/calculations/motor")({
  component: MotorPage,
});

function MotorPage() {
  const [phase, setPhase] = useState<"1" | "3">("3");
  const [powerKw, setPowerKw] = useState<number | "">("");
  const [voltage, setVoltage] = useState<number | "">(400);
  const [eff, setEff] = useState<number | "">(0.9);
  const [cosPhi, setCos] = useState<number | "">(0.85);

  const result = useMemo(() => {
    if ([powerKw, voltage, eff, cosPhi].some((v) => v === "")) return null;
    try {
      return motorCurrent({
        phase: Number(phase) as Phase,
        power: (powerKw as number) * 1000,
        voltage: voltage as number,
        efficiency: eff as number,
        cosPhi: cosPhi as number,
      });
    } catch (e) {
      return { error: (e as Error).message };
    }
  }, [phase, powerKw, voltage, eff, cosPhi]);

  return (
    <>
      <Link to="/calculations" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Beregninger
      </Link>
      <PageHeader title="Motor" subtitle="Beregner merkestrøm og anslag for startstrøm." />
      <CalcShell
        title="Inndata"
        result={
          result && "error" in result ? (
            <div className="card p-5 text-sm text-rose-600">{result.error}</div>
          ) : result ? (
            <>
            <ResultCard
              title="Motorstrøm"
              rows={[
                { label: "Merkestrøm I", value: `${fmt(result.current, 2)} A`, strong: true },
                { label: "Startstrøm (~6×)", value: `${fmt(result.startingCurrent, 1)} A` },
                { label: "Elektrisk inn", value: `${fmt(result.inputPower / 1000, 2)} kW` },
              ]}
              note="Startstrøm er et grovt anslag; faktisk verdi avhenger av motortype og starter."
            />
            <div className="mt-3 flex justify-end">
              <AddToQuoteButton sourceLabel={`Motorberegning`} lines={linesFromElectricianNote(`Motor: dimensjonering`)} />
            </div>
          </>
          ) : <div className="card p-5 text-sm text-muted-foreground">Fyll ut alle felt.</div>
        }
      >
        <Field label="Fase">
          <Select value={phase} onChange={setPhase} options={[{ value: "1", label: "1-fase" }, { value: "3", label: "3-fase" }]} />
        </Field>
        <Field label="Akselytelse" unit="kW"><NumberInput value={powerKw} onChange={setPowerKw} /></Field>
        <Field label="Spenning" unit="V"><NumberInput value={voltage} onChange={setVoltage} /></Field>
        <Field label="Virkningsgrad η"><NumberInput value={eff} onChange={setEff} /></Field>
        <Field label="cosφ"><NumberInput value={cosPhi} onChange={setCos} /></Field>
      </CalcShell>
    </>
  );
}
